import { useState, useRef, RefObject } from 'react'
import { ImagePlusIcon } from 'lucide-react'
import { UploadFileProps } from '@wellesley/ui'
import { UploadItem } from '@wellesley/uploads'
import { ToolbarButton } from '../ToolbarButton'
import { MarkdownActions } from '../MarkdownEditorState/actions'
import { InsertMediaDialog } from '../InsertMediaDialog'

export type MarkdownAddMediaButtonProps = {
  onAddMedia(files: UploadFileProps[]): void
  actions: MarkdownActions
  attachments?: UploadItem[]
  disabled?: boolean
  editorRef?: RefObject<HTMLTextAreaElement>
}

export const MarkdownAddMediaButton = ({
  onAddMedia,
  actions,
  attachments,
  disabled,
  editorRef,
}: MarkdownAddMediaButtonProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [anchorRect, setAnchorRect] = useState<DOMRect>()
  const savedSelectionRef = useRef<{ start: number; end: number } | null>(null)
  const actionsRef = useRef(actions)
  actionsRef.current = actions

  const openDialog = () => {
    if (disabled) return
    const el = editorRef?.current
    if (el) {
      const start = el.selectionStart ?? el.value.length
      const end = el.selectionEnd ?? start
      savedSelectionRef.current = { start, end }
    }
    if (buttonRef.current) {
      setAnchorRect(buttonRef.current.getBoundingClientRect())
    }
    setIsDialogOpen(true)
  }

  const handleWrapperClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    openDialog()
  }

  const handleInsertUploads = (items: UploadItem[]) => {
    if (items.length === 0) return

    const images = items
      .map((item) =>
        item.uploadId
          ? {
              url: `/${item.uploadId}`,
              altText: item?.meta?.name || 'Attachment',
            }
          : null,
      )
      .filter((img): img is { url: string; altText: string } => img !== null)

    if (images.length > 0) {
      setTimeout(() => {
        const el = editorRef?.current
        if (el && savedSelectionRef.current) {
          el.focus()
          el.setSelectionRange(
            savedSelectionRef.current.start,
            savedSelectionRef.current.end,
          )
        }
        actionsRef.current.insertImages(images)
      }, 0)
    }
  }

  const handleInsertExternalUrl = (url: string) => {
    const el = editorRef?.current
    if (el && savedSelectionRef.current) {
      el.focus()
      el.setSelectionRange(
        savedSelectionRef.current.start,
        savedSelectionRef.current.end,
      )
    }
    actionsRef.current.insertImage(url)
  }

  const handleInsertAttachments = async (items: UploadItem[]) => {
    if (items.length === 0) return

    const images = (
      await Promise.all(
        items.map(async (item) => {
          const uploadId = await item.upload
          if (!uploadId) return null
          return {
            url: `/${uploadId}`,
            altText: item?.meta?.name || 'Attachment',
          }
        }),
      )
    ).filter((img): img is { url: string; altText: string } => img !== null)

    if (images.length > 0) {
      const el = editorRef?.current
      if (el && savedSelectionRef.current) {
        el.focus()
        el.setSelectionRange(
          savedSelectionRef.current.start,
          savedSelectionRef.current.end,
        )
      }
      actionsRef.current.insertImages(images)
    }
  }

  const handleAllCanceled = () => {
    actions.insertText('')
  }

  const handleDialogClose = () => {
    setIsDialogOpen(false)
    document.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }),
    )
  }

  const dialog = isDialogOpen && (
    <InsertMediaDialog
      onAddMedia={onAddMedia}
      attachments={attachments}
      anchorRect={anchorRect}
      onClose={handleDialogClose}
      onInsertUploads={handleInsertUploads}
      onInsertExternalUrl={handleInsertExternalUrl}
      onInsertAttachments={(uploads) => void handleInsertAttachments(uploads)}
      onAllCanceled={handleAllCanceled}
    />
  )

  return (
    <div className="inline-block" onClick={handleWrapperClick}>
      <ToolbarButton
        ref={buttonRef}
        Icon={ImagePlusIcon}
        ariaLabel="Add Media"
        disabled={disabled}
      />
      {dialog}
    </div>
  )
}
